import { portalText } from "../../i18n/portal.js";
/**
 * The allowed-site rules the origins editor checks as you type (plan-004 WC-81, design §17.6.3).
 *
 * ONE RULE, NOT TWO. Whether an address is accepted is `normaliseOrigin` from `@wetalk/domain` —
 * the very function the API normalises with before it writes `channel_binding_origin`, and the
 * runtime checks `Origin` with. A second spelling here would let the editor accept what the API
 * refuses, or the other way round. This module only adds the WHY: when `normaliseOrigin` refuses,
 * it names the one thing to change, in plain words, so the owner is not left guessing.
 *
 * AN ORIGIN CHECK IS NOT AUTHENTICATION. It stops the widget being put on somebody else's site,
 * because a browser sends an honest `Origin`; anything that is not a browser can send any header.
 * The editor says so beside the list.
 */
import { normaliseOrigin } from "@wetalk/domain";

/**
 * At most this many allowed sites per widget, after duplicates are removed — the API's bound
 * (`CHANNEL_BINDING_ORIGIN_MAX` in `packages/db`, 400 above it). A product bound, not configuration.
 */
export const WEBSITE_WIDGET_ORIGIN_MAX = 20;

/** The rules, in the order the editor prints them. */
export const ORIGIN_RULE = () => ([
  portalText("operations", "start_with_https_plain_http_is_accepted_only_for_localhost_7d4a4caf"),
  portalText("operations", "the_site_s_address_only_the_host_name_and_if_it_has_one_th_d27cfe91"),
  portalText("operations", "letters_are_made_lower_case_and_the_default_port_is_droppe_5f21256d"),
  portalText("operations", "www_roma_example_and_roma_example_are_different_sites_add_13932606"),
  portalText("operations", "at_most_value_sites_324f4d4c", { v0: String(String(WEBSITE_WIDGET_ORIGIN_MAX)) }),
]);

export type OriginAdvice =
  | { readonly kind: "empty" }
  | { readonly kind: "accepted"; readonly origin: string }
  | { readonly kind: "refused"; readonly problem: string };

/** What the editor says about `input` while it is typed. Acceptance is `normaliseOrigin`'s alone. */
export function originAdviceOf(input: string): OriginAdvice {
  const trimmed = input.trim();
  if (trimmed === "") return { kind: "empty" };
  const origin = normaliseOrigin(trimmed);
  if (origin !== null) return { kind: "accepted", origin };
  return { kind: "refused", problem: refusalOf(trimmed) };
}

function refusalOf(trimmed: string): string {
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return portalText("operations", "start_with_https_for_example_https_value_a1f56b12", { v0: String(trimmed.replace(/^\/+/, "")) });
  }
  if (trimmed.includes("?") || trimmed.includes("#")) {
    return portalText("operations", "remove_everything_from_the_or_onwards_only_the_site_s_addr_20dc4613");
  }
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return portalText("operations", "this_is_not_a_web_address_use_one_such_as_https_www_exampl_bd0cbc16");
  }
  if (parsed.protocol === "http:") {
    return portalText("operations", "use_https_plain_http_is_accepted_only_for_localhost_for_tr_f4fc890a");
  }
  if (parsed.protocol !== "https:") {
    return portalText("operations", "a_site_address_starts_with_https_not_value_b282000a", { v0: String(parsed.protocol) });
  }
  if (parsed.username !== "" || parsed.password !== "") {
    return portalText("operations", "remove_the_name_and_password_before_the_only_the_site_s_ad_7ccc3089");
  }
  if (parsed.pathname !== "" && parsed.pathname !== "/") {
    return portalText("operations", "remove_the_page_path_value_the_chat_is_allowed_on_the_whol_bb931964", { v0: String(parsed.pathname) });
  }
  if (parsed.hostname.startsWith("[")) {
    return portalText("operations", "an_ipv6_address_cannot_be_listed_use_the_site_s_host_name_75869514");
  }
  return portalText("operations", "this_host_name_has_characters_a_site_address_cannot_have_s_b6fa8bda");
}

/** Two origin sets are the same set (both are kept sorted and de-duplicated). */
export function sameOriginSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((one, at) => one === sortedRight[at]);
}
